import React, { useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import PaginatedTable from "../../../../../shared/PaginatedTable";
import StudentRewardsTableHeader from "./StudentRewardsTableHeader";
import StudentRewardsTableRow from "./StudentRewardsTableRow";

import EduApi from "../../../../../../services/resources/EduApi";
import { ApiCallErrorMessageHandler } from "../../../../../../lib/coc-common-scripts";
import axios from "axios";
import { formatNumber } from "../../../../../../lib";
import ApproveRejectRewardRequestModal from "../../../../rewardRequests/ApproveRejectRewardRequestModal";

const actionTypes = {
  Approve: "approve",
  Reject: "reject",
};

export default function StudentRewardsTable(props) {
  const {
    eduStudent,
    eduStudent: {
      eduProgramID,
      eduStudentEnrollmentID,
      numOfCreditsAvailable,
      numOfCreditsLifetime,
    },
    refreshEduStudent,
    viewOnly,
  } = props;

  const [approveRejectRewardRequest, setApproveRejectRewardRequest] = useState({
    action: null,
    errorMessage: "",
    loading: false,
    rewardRequest: null,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(12);
  const [rewardRequests, setRewardRequests] = useState([]);
  const [
    showApproveRejectRewardRequestConfirmationModal,
    setShowApproveRejectRewardRequestConfirmationModal,
  ] = useState(false);
  const [success, setSuccess] = useState(true);
  const [totalRewardRequests, setTotalRewardRequests] = useState(0);

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  const getRewardRequests = (_page = 1, _results = results) => {
    unstable_batchedUpdates(() => {
      setLoading(true);
      setPage(_page);
      setResults(_results);
      setSuccess(true);
    });

    EduApi.getEduProgramRewardRequests(
      apiSignalRef.current.token,
      eduProgramID,
      _page,
      _results,
      undefined,
      {
        eduStudentEnrollmentId: eduStudentEnrollmentID,
      },
    )
      .then(({ rewardRequests, totalRewardRequests }) => {
        unstable_batchedUpdates(() => {
          setLoading(false);
          setRewardRequests(rewardRequests);
          setTotalRewardRequests(totalRewardRequests);
          setSuccess(true);
        });
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          unstable_batchedUpdates(() => {
            setLoading(false);
            setRewardRequests([]);
            setSuccess(false);
            setTotalRewardRequests(0);
          });
        }
      });
  };

  const toggleApproveRejectRewardRequest = (rewardRequest, approve) => {
    setApproveRejectRewardRequest({
      action:
        approve === true
          ? actionTypes.Approve
          : approve === false
            ? actionTypes.Reject
            : null,
      errorMessage: "",
      loading: false,
      rewardRequest: rewardRequest || null,
    });
    setShowApproveRejectRewardRequestConfirmationModal((prev) => !prev);
  };

  const doApproveRejectRewardRequest = async () => {
    setApproveRejectRewardRequest({
      ...approveRejectRewardRequest,
      errorMessage: "",
      loading: true,
    });

    try {
      const { rewardRequest } = approveRejectRewardRequest;
      const updatedRewardRequest = {
        id: rewardRequest.id,
        eduRewardID: rewardRequest.eduRewardID,
        eduStudentEnrollmentID: rewardRequest.eduStudentEnrollmentID,
        //  BE only updates status field, no need to set other irrelevant fields for post
        status:
          approveRejectRewardRequest.action === actionTypes.Approve
            ? "Approved"
            : "Rejected",
      };

      await EduApi.submitEduStudentRewardRequest(
        apiSignalRef.current.token,
        eduStudent.eduProgramID,
        updatedRewardRequest,
      );
    } catch (err) {
      setApproveRejectRewardRequest({
        ...approveRejectRewardRequest,
        errorMessage: ApiCallErrorMessageHandler(err),
        loading: false,
      });
      return;
    }

    if (approveRejectRewardRequest.action === actionTypes.Reject) {
      refreshEduStudent(); //refresh student avail credits info after rejecting a credit
    }
    getRewardRequests(page, results);
    toggleApproveRejectRewardRequest();
  };

  const isApproving = approveRejectRewardRequest.action === actionTypes.Approve;
  const isRejecting = approveRejectRewardRequest.action === actionTypes.Reject;
  const isRejectingApprovedRequest =
    isRejecting &&
    approveRejectRewardRequest.rewardRequest?.status === "Approved";

  return (
    <div>
      <div className="flex flex-justify-space flex-align-center mb-24">
        <p className="fw-500 xxl-text mb-24">Reward History</p>
        <div className="flex">
          <p className="medium-text">
            <span className="fw-700">Total Credits</span>{" "}
            {formatNumber(numOfCreditsLifetime)}
          </p>
          <p className="medium-text ml-24">
            <span className="fw-700">Available Credits</span>{" "}
            {formatNumber(numOfCreditsAvailable)}
          </p>
        </div>
      </div>
      <PaginatedTable
        loading={loading}
        loadData={getRewardRequests}
        page={page}
        records={rewardRequests}
        renderHeader={() => <StudentRewardsTableHeader />}
        renderRow={(rewardRequest, index) => (
          <StudentRewardsTableRow
            key={index}
            onApproveRejectRewardRequest={toggleApproveRejectRewardRequest}
            rewardRequest={rewardRequest}
            viewOnly={viewOnly}
          />
        )}
        results={results}
        showResultsCount={true}
        success={success}
        totalCount={totalRewardRequests}
      />

      {showApproveRejectRewardRequestConfirmationModal && (
        <ApproveRejectRewardRequestModal
          approveRejectRewardRequest={approveRejectRewardRequest}
          onCancel={toggleApproveRejectRewardRequest}
          onConfirm={doApproveRejectRewardRequest}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isRejectingApprovedRequest={isRejectingApprovedRequest}
        />
      )}
    </div>
  );
}
